import { useRef, useState, type FormEvent } from 'react';
import { ApiError } from '../../api';
import { ErrorLine } from '../../components/Lines';
import {
  checkPasswords,
  NO_PASSWORDS,
  PasswordFields,
  type PasswordValues,
} from '../../components/PasswordFields';
import { errorText } from '../../errors';
import { t } from '../../i18n';
import { useMember, useSession } from '../../session';

/**
 * The forced change (D5, D20, D21): a member who logged in with a one-time password, admins too,
 * chooses their own before anything else. Straight from the login, the password just typed is the
 * current one, so only the new one is asked, twice; opened later (the app closed and opened
 * again), the current one is asked too. The guard (Gate.tsx) moves on to Home once it's saved.
 * Its fields and checks are Profile's change of password's too (PasswordFields; A6).
 */
export function ChangePassword() {
  const member = useMember();
  const { oneTimePassword, changePassword, forgetOneTimePassword, logout } = useSession();
  const [values, setValues] = useState<PasswordValues>(NO_PASSWORDS);
  const [busy, setBusy] = useState<'save' | 'logout'>();
  const [error, setError] = useState<string>();
  const currentRef = useRef<HTMLInputElement>(null);
  const nextRef = useRef<HTMLInputElement>(null);
  const repeatRef = useRef<HTMLInputElement>(null);
  const askCurrent = oneTimePassword === undefined;

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    // The checks the phone can make, before asking the server: an empty field, the rule, the two
    // alike.
    const checked = checkPasswords(values, askCurrent);
    if (checked.kind === 'empty') {
      const refs = { current: currentRef, next: nextRef, repeat: repeatRef };
      refs[checked.field].current?.focus();
      return;
    }
    if (checked.kind === 'error') {
      setError(t(checked.code));
      return;
    }
    setBusy('save');
    setError(undefined);
    try {
      await changePassword(oneTimePassword ?? values.current, values.next);
    } catch (err) {
      // The password kept from the login isn't the current one any more (a reset meanwhile):
      // the member types it.
      if (err instanceof ApiError && err.code === 'wrong_password') forgetOneTimePassword();
      setError(errorText(err));
      setBusy(undefined);
    }
  }

  async function onLogout() {
    if (busy) return;
    setBusy('logout');
    setError(undefined);
    try {
      await logout();
    } catch (err) {
      setError(errorText(err));
      setBusy(undefined);
    }
  }

  return (
    <main className="auth">
      <h1>{t('password.title')}</h1>
      <p className="muted">{t('password.intro')}</p>
      <form className="form" onSubmit={onSubmit} noValidate>
        <PasswordFields
          username={member.username}
          askCurrent={askCurrent}
          values={values}
          onChange={setValues}
          currentRef={currentRef}
          nextRef={nextRef}
          repeatRef={repeatRef}
        />
        <ErrorLine text={error} />
        <button type="submit" className="button" disabled={busy !== undefined}>
          {busy === 'save' ? t('password.submitting') : t('password.submit')}
        </button>
      </form>
      <button
        type="button"
        className="button button-quiet"
        onClick={onLogout}
        disabled={busy !== undefined}
      >
        {t('shell.logout')}
      </button>
    </main>
  );
}
